/**
 * Pure decision helper for the Step 2 Final Price Bar.
 *
 * Extracted so the rendering rules are unit-testable without spinning
 * up a React renderer. The component imports `pickFinalPriceBarMode`
 * and chooses its visual variant from the returned mode string.
 *
 * Modes:
 *   'empty'   — no services selected, no preview → "Final price: 0 EUR"
 *   'loading' — preview is in flight while services are selected
 *   'price'   — show the final amount (Row 1: label, Row 2: 3xl amount
 *               + struck standard + tariff pill, Row 3: balance).
 *
 * Per UX request (2026-02-26), the customer-facing bar NEVER shows
 * a "Price was updated" warning. The component reflects this by
 * passing `previewError: null` to this helper so an outstanding
 * price-changed signal cannot promote the bar to an error variant.
 * The 409 from the backend is still logged via console.warn for
 * debugging.
 */
export function pickFinalPriceBarMode({
  previewError,
  previewLoading,
  selectedCount,
  finalTotal,
}) {
  const isEmpty = !selectedCount;
  const hasAmount = finalTotal != null;

  // Error fallback only when there is literally nothing else to show
  // (no services AND no final total). Production code passes
  // `previewError=null` so this branch is unreachable in the live UI,
  // but the helper keeps the contract for future maintainers /
  // potential admin-side reuse.
  if (previewError && (isEmpty || !hasAmount)) return 'error-only';

  // Loading takes precedence over empty so the user sees activity.
  if (previewLoading && selectedCount > 0) return 'loading';

  if (isEmpty) return 'empty';
  return 'price';
}
